import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  deDE,
} from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import { Add, Check, Clear } from '@mui/icons-material';
import { UserFullEdit } from './UserFullEdit';
import { IUser } from '../../../Interfaces/IUser';
import { IUserGroup } from '../../../Interfaces/IUserGroup';
import { IDayOffWorkCategory } from '../../../Interfaces/IDayOffWorkCategory';
import {
  useGetDaysOffWorkCategories,
  useGetRoles,
  useGetUserGroups,
  useGetUsers,
} from '../../../hooks/data';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { IRole } from '../../../Interfaces/IRole';
import { ShowWasSaved } from '../../../core/ShowWasSaved';
import { FullScreenDialog } from '../../../core/FullScreenDialog';
import { UserSearch } from './UserSearch';

type IProps = {
  initialUsers: IUser[];
  roles: IRole[];
  userGroups: IUserGroup[];
  daysOffWorkCategories: IDayOffWorkCategory[];
};

export const UserOverview = () => {
  const { daysOffWorkCategories, isLoadingDaysOffWorkCategories } =
    useGetDaysOffWorkCategories();
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();
  const { users, isLoadingUsers } = useGetUsers();
  const { roles, isLoadingRoles } = useGetRoles();

  if (
    isLoadingDaysOffWorkCategories ||
    isLoadingUserGroups ||
    isLoadingUsers ||
    isLoadingRoles
  ) {
    return <LoadingSpinner />;
  }

  if (!daysOffWorkCategories || !userGroups || !users || !roles) {
    return <Alert severity='error'>Fehler!</Alert>;
  }

  return (
    <Users
      initialUsers={users}
      roles={roles}
      userGroups={userGroups}
      daysOffWorkCategories={daysOffWorkCategories}
    />
  );
};

export const Users = ({
  initialUsers,
  roles,
  userGroups,
  daysOffWorkCategories,
}: IProps) => {
  const [users, setUsers] = useState<IUser[]>(initialUsers);
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>(initialUsers);
  const [showDeactivated, setShowDeactivated] = useState<boolean>(false);

  const [idUser, setIdUser] = useState<number | null>(null);
  const [wasSaved, setWasSaved] = useState(false);
  const [wasSavedSuccesfully, setWasSavedSuccesfully] = useState(false);

  useEffect(() => {
    setUsers(initialUsers);
    setFilteredUsers(initialUsers);
  }, [initialUsers]);

  const gridRows = useMemo(() => {
    if (showDeactivated) return filteredUsers;
    return filteredUsers.filter(u => !Boolean(u.isBlocked));
  }, [filteredUsers, showDeactivated]);

  const columns: GridColDef[] = [
    { field: 'idUser', headerName: 'Nr.', width: 90 },
    {
      field: 'isBlocked',
      headerName: 'Aktiv',
      width: 90,
      renderCell: (params: GridRenderCellParams) =>
        Boolean(params.row.isBlocked) ? (
          <Clear color='error' />
        ) : (
          <Check color='success' />
        ),
    },
    {
      field: 'isNotary',
      headerName: 'Notar',
      width: 90,
      renderCell: (params: GridRenderCellParams) =>
        Boolean(params.row.isNotary) ? <Check color='success' /> : <Clear />,
    },
    {
      field: 'Role',
      headerName: 'Rolle',
      width: 150,
      valueGetter: params => {
        const role = roles.find(r => r.idRole === params.row.idRole);
        return role?.Role ?? '';
      },
    },
    { field: 'Login', headerName: 'Benutzername', width: 150 },
    { field: 'FirstName', headerName: 'Vorname', width: 150 },
    { field: 'LastName', headerName: 'Nachname', width: 150 },
    {
      field: 'UserBadge',
      headerName: 'Kürzel/Farbe',
      width: 150,
      sortable: false,
      renderCell: params => {
        const { Initials, ColorHex } = params.row;
        const hasInitials = Boolean(Initials);
        return (
          <Chip
            size='small'
            label={hasInitials ? Initials : 'Kein Kürzel'}
            sx={{
              width: 90,
              justifyContent: 'center',
              backgroundColor: ColorHex,
              color: '#fff',
              fontStyle: hasInitials ? 'normal' : 'italic',
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      <ShowWasSaved
        wasSaved={wasSaved}
        setWasSaved={setWasSaved}
        wasSuccessfullySaved={wasSavedSuccesfully}
      />
      <FullScreenDialog
        title='Benutzer'
        isOpen={idUser !== null}
        onClose={() => setIdUser(null)}
      >
        <>
          {idUser !== null && (
            <UserFullEdit
              key={`idUSer-${idUser}`}
              idUser={idUser}
              setIdUser={setIdUser}
              userArray={users}
              userGroupArray={userGroups}
              setUserArray={setUsers}
              roleArray={roles}
              setWasSaved={setWasSaved}
              setWasSavedSuccesfully={setWasSavedSuccesfully}
              dayOffWorkCategoryArray={daysOffWorkCategories}
            />
          )}
        </>
      </FullScreenDialog>

      <Box sx={{ m: 2, mt: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='h4'>Benutzer</Typography>

            <FormControlLabel
              sx={{ m: 0 }}
              label='Deaktivierte anzeigen'
              control={
                <Switch
                  size='small'
                  checked={showDeactivated}
                  onChange={e => setShowDeactivated(e.target.checked)}
                />
              }
            />
          </Box>

          <IconButton onClick={() => setIdUser(-1)}>
            <Add />
          </IconButton>
        </Box>

        <UserSearch
          users={users}
          setFilteredUsers={setFilteredUsers}
          roles={roles}
        />

        <Box mt={6} mb={1}>
          {`${gridRows.length} von ${users.length} Benutzer`}
        </Box>

        <DataGrid
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={gridRows}
          columns={columns}
          getRowId={row => row.idUser}
          onRowClick={params => setIdUser(Number(params.id))}
        />
      </Box>
    </>
  );
};
